import { z } from 'zod';
import { diaCivil, instanteDeHoraLocal } from './zona-horaria.js';
/**
 * Fases del programa · de qué día a qué día va cada una.
 *
 * Los límites salen del propio protocolo (`pasos-12meses-protocolo.html`), que
 * los declara en su tabla de resumen: **M1–M3 es D+0 a D+84** —doce semanas, la
 * última de las cuales es la evaluación trimestral— y **M4–M6 cierra en D+180**
 * con la evaluación semestral. Los dos siguientes se derivan de los hitos: M11
 * tiene su check-in en D+336 y M12 es el cierre anual de D+360, así que el corte
 * cae entre los dos.
 *
 * **`EXTENSION` es nueva y cierra P-45.** El programa puede continuar después de
 * D+360 con las características de consolidación (definición de Fundación del
 * 06/08). Hasta ahora el enum llegaba hasta `M12`, así que un evento emitido
 * durante la extensión quedaba etiquetado como si fuera del cierre anual: no del
 * todo falso, pero tampoco lo que estaba pasando. **No es un séptimo cierre**: el
 * programa sigue abierto y se cierra después, por alguno de los seis.
 */
export const FASES_DEL_PROGRAMA = ['M1_M3', 'M4_M6', 'M7_M11', 'M12', 'EXTENSION'];
/**
 * Último día de cada fase, contado desde el egreso (día 0).
 *
 * `EXTENSION` no tiene último día acá: lo fija la duración que el equipo decida
 * al extender, y por eso el programa extendido sabe cuándo termina y la fase no.
 */
export const ULTIMO_DIA_DE_FASE = Object.freeze({
    M1_M3: 84,
    M4_M6: 180,
    M7_M11: 336,
    M12: 360,
});
/**
 * La fase que corresponde a un día del programa.
 *
 * Determinística y sin estado: es la misma función que usan el generador del
 * plan, el job de cadencia y la resolución de la fase de cada evento. Que haya
 * una sola es lo que evita que un evento diga una fase y el plan otra.
 */
export function faseDelDia(diaDelPrograma) {
    if (diaDelPrograma <= ULTIMO_DIA_DE_FASE.M1_M3)
        return 'M1_M3';
    if (diaDelPrograma <= ULTIMO_DIA_DE_FASE.M4_M6)
        return 'M4_M6';
    if (diaDelPrograma <= ULTIMO_DIA_DE_FASE.M7_M11)
        return 'M7_M11';
    if (diaDelPrograma <= ULTIMO_DIA_DE_FASE.M12)
        return 'M12';
    return 'EXTENSION';
}
/**
 * La fase que corresponde de verdad, mirando si el programa fue extendido.
 *
 * `faseDelDia` es calendario puro y después de D+360 dice `EXTENSION`. Pero un
 * programa que llegó al día 361 **sin que nadie lo extendiera** no está en
 * extensión: está terminado y esperando su cierre. Etiquetar sus eventos como
 * `EXTENSION` diría que hay seguimiento donde no lo hay — el mismo error que
 * P-45 corrigió al revés.
 */
export function faseVigente(dia, extensionDias) {
    const fase = faseDelDia(dia);
    if (fase !== 'EXTENSION')
        return fase;
    return extensionDias === null ? 'M12' : 'EXTENSION';
}
const MS_POR_DIA = 24 * 60 * 60 * 1000;
/**
 * El egreso como fecha de calendario.
 *
 * El egreso es una **fecha**, no un instante: el alta lo pide como fecha y la
 * base lo guarda en una columna `date`, que llega como la medianoche UTC de ese
 * día. Por eso se leen sus componentes UTC y no los de la zona: llevar esa
 * medianoche a Buenos Aires daría el día anterior. Un egreso que nace de un
 * instante (el arranque del programa) se convierte antes con `diaCivil`.
 */
function fechaDelEgreso(egreso) {
    return Date.UTC(egreso.getUTCFullYear(), egreso.getUTCMonth(), egreso.getUTCDate());
}
/**
 * Días completos entre el egreso y un instante. El día del egreso es el 0.
 *
 * **El día se cuenta en la zona de la institución** (P-131, decisión de Cowork
 * del 03/11): es una unidad de calendario de la institución, igual que el plan.
 * Hasta CORRECCIONES-1 se comparaban medianoches UTC, y entre las 21 y la
 * medianoche de Buenos Aires el Inicio decía el día siguiente y "hoy" para un
 * contacto que abría mañana a las 9. La zona no tiene valor por defecto a
 * propósito: una llamada sin zona no compila.
 */
export function diaDelPrograma(egreso, fecha, zonaHoraria) {
    return Math.round((diaCivil(fecha, zonaHoraria).getTime() - fechaDelEgreso(egreso)) / MS_POR_DIA);
}
/**
 * El instante en que, en la zona de la institución, son las `hora` del día
 * `dia` del programa.
 *
 * Es lo que usa el plan para la apertura de cada contacto, y lo que hace que un
 * check-in que abre "el día 35 a las 9" sea el mismo día 35 que muestra el
 * Inicio a las 23: los dos cuentan el mismo calendario.
 */
export function instanteDelDia(egreso, dia, hora, zonaHoraria) {
    return instanteDeHoraLocal(new Date(fechaDelEgreso(egreso) + dia * MS_POR_DIA), hora, zonaHoraria);
}
/** El instante en que empieza un día del programa: su medianoche en la zona de la institución. */
export function fechaDelDia(egreso, dia, zonaHoraria) {
    return instanteDelDia(egreso, dia, 0, zonaHoraria);
}
/**
 * El día 0 de un programa: el egreso o, si todavía no está cargado, el día en
 * que arrancó el programa, contado en la zona de la institución.
 *
 * `iniciadoAt` es un instante: un programa que arranca a las 23:30 de Buenos
 * Aires arranca ese día, no el siguiente, aunque en UTC ya sea mañana.
 */
export function diaCeroDelPrograma(egresoAt, iniciadoAt, zonaHoraria) {
    return egresoAt ?? diaCivil(iniciadoAt, zonaHoraria);
}
/**
 * El primer semestre exige más: el protocolo pide recontacto a las 24 horas y
 * en el segundo lo relaja a 72, "porque el paciente está en consolidación".
 */
export function esPrimerSemestre(fase) {
    return fase === 'M1_M3' || fase === 'M4_M6';
}
export const faseSchema = z.enum(FASES_DEL_PROGRAMA);
//# sourceMappingURL=fases.js.map